import React from 'react';
import { Link } from 'react-router-dom';
import { FiRotateCcw, FiClock, FiShield, FiCheck, FiX } from 'react-icons/fi';
import SEO from '../components/SEO';
import { trackEvent } from '../components/GoogleAnalytics';
import { useEffect } from 'react';

export default function Returns() {
  useEffect(() => {
    trackEvent('page_view', 'navigation', 'returns_policy');
  }, []);

  return (
    <>
      <SEO
        title="Returns & Exchanges Policy - Bailebelle"
        description="Easy returns and exchanges at Bailebelle. Free 30-day returns with prepaid labels. Shop with confidence."
        keywords="returns, exchanges, return policy, refund policy, free returns"
        url="/returns"
      />
      <div className="min-h-screen bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'serif' }}>
              Returns & Exchanges
            </h1>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              We want you to love your purchase! Our easy return and exchange policy ensures your complete satisfaction.
            </p>
            <div className="mt-6 p-4 bg-gray-900 rounded-xl border border-gray-800 inline-block">
              <h3 className="text-pink-500 font-bold mb-2">Cancellation Policy</h3>
              <p className="text-gray-300">
                Orders can be cancelled within 48 Hours of placing the order. A service fee of 10-15% will be charged for cancellations.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Return Policy */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">Return Policy</h2>

              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <FiRotateCcw className="text-pink-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">30-Day Return Window</h3>
                    <p className="text-gray-400 mb-3">
                      You have 30 days from the delivery date to return your items for a full refund.
                    </p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li>• Items must be in original condition</li>
                      <li>• Tags must be attached</li>
                      <li>• Items must be unworn and unwashed</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <FiShield className="text-pink-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Free Returns</h3>
                    <p className="text-gray-400 mb-3">
                      Returns are completely free within the United States.
                    </p>
                    <p className="text-gray-400 text-sm">
                      We'll provide a prepaid return label for your convenience.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <FiClock className="text-pink-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Refund Processing</h3>
                    <p className="text-gray-400 mb-3">
                      Refunds are processed within 5-7 business days after we receive your return.
                    </p>
                    <p className="text-gray-400 text-sm">
                      You'll receive an email confirmation once your refund is processed.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Exchange Policy */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">Exchange Policy</h2>

              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <FiCheck className="text-pink-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Easy Exchanges</h3>
                    <p className="text-gray-400 mb-3">
                      Need a different size or color? We make exchanges simple and free.
                    </p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li>• Free exchanges for size or color changes</li>
                      <li>• Same 30-day window applies</li>
                      <li>• We'll send your new item immediately</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <FiShield className="text-pink-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Exchange Process</h3>
                    <p className="text-gray-400 mb-3">
                      Our streamlined exchange process gets you the right item quickly.
                    </p>
                    <ol className="text-gray-400 space-y-1 text-sm list-decimal list-inside">
                      <li>Contact us to initiate the exchange</li>
                      <li>We'll send your new item right away</li>
                      <li>Return the original item using our prepaid label</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How to Return */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">How to Return or Exchange</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Contact Us</h3>
                <p className="text-gray-400 text-sm">
                  Contact us through our contact form or call us to start your return/exchange
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Get Your Label</h3>
                <p className="text-gray-400 text-sm">
                  We'll email you a prepaid return label and return instructions
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Package & Ship</h3>
                <p className="text-gray-400 text-sm">
                  Package your items securely and drop off at any authorized location
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">4</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Receive Refund</h3>
                <p className="text-gray-400 text-sm">
                  Get your refund or exchange item within 5-7 business days
                </p>
              </div>
            </div>
          </div>

          {/* What Can't Be Returned */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gray-900 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <FiX className="text-red-500 mt-1" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Items We Cannot Accept</h3>
                  <ul className="text-gray-400 space-y-1 text-sm">
                    <li>• Items worn or washed</li>
                    <li>• Items without original tags</li>
                    <li>• Items damaged by misuse</li>
                    <li>• Custom or personalized items</li>
                    <li>• Items returned after 30 days</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <FiCheck className="text-green-500 mt-1" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Items We Accept</h3>
                  <ul className="text-gray-400 space-y-1 text-sm">
                    <li>• Unworn items with tags</li>
                    <li>• Items in original packaging</li>
                    <li>• Items within 30 days</li>
                    <li>• Regular stock items</li>
                    <li>• Items in sellable condition</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">Need Help with Your Return?</h2>
              <p className="text-gray-400 mb-6">
                Our customer service team is here to help make your return or exchange as smooth as possible.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
